import type {
  CreateWidget,
  HomeBoard,
  HomeDashboard,
  HomeWidget,
  UpdateWidget,
} from "@ronin/contracts";
import { toast } from "@ronin/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api.js";
import { showError } from "../../lib/errors.js";
import { widgetLabel } from "./widget-tile.js";

export const homeQuery = queryOptions({ queryKey: ["home"], queryFn: () => api.home.get() });

/** spec14 FR3: which optional widgets this server offers (false until known). */
export function useHomeFeatures() {
  const { data } = useQuery(homeQuery);
  return data?.features ?? { docker: false, serverStats: false };
}

/** Dashboard data and mutations (spec12 FR12: saved at once, rolled back on errors). */
export function useHome() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: homeQuery.queryKey });

  /** Shows a change at once; a failure refetches the server state. */
  function optimistic(change: (dashboard: HomeDashboard) => HomeDashboard) {
    void queryClient.cancelQueries({ queryKey: homeQuery.queryKey });
    queryClient.setQueryData(homeQuery.queryKey, (data) => data && change(data));
  }
  /** Changes every widget list: each board's grid and the shared top grid (FR9a). */
  const onWidgets = (change: (widgets: HomeWidget[]) => HomeWidget[]) =>
    optimistic((d) => ({
      ...d,
      boards: d.boards.map((b) => ({ ...b, widgets: change(b.widgets) })),
      top: change(d.top),
    }));

  const query = useQuery(homeQuery);

  const createBoard = useMutation({
    mutationFn: (name: string) => api.home.createBoard({ name }),
    onSuccess: refresh,
    onError: showError("the board"),
  });
  const renameBoard = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.home.renameBoard(id, { name }),
    onSuccess: refresh,
    onError: showError("the board"),
  });
  const restoreBoard = useMutation({
    mutationFn: (id: string) => api.home.restoreBoard(id),
    onSuccess: refresh,
    onError: showError("the board", "restored"),
  });
  const removeBoard = useMutation({
    mutationFn: (board: HomeBoard) => api.home.removeBoard(board.id),
    onSuccess: (_result, board) => {
      void refresh();
      toast("Board deleted", {
        description: board.name,
        duration: 8000,
        action: { label: "Undo", onClick: () => restoreBoard.mutate(board.id) },
      });
    },
    onError: showError("the board", "deleted"),
  });
  const orderBoards = useMutation({
    mutationFn: (ids: string[]) => api.home.orderBoards(ids),
    onMutate: (ids) =>
      optimistic((d) => ({
        ...d,
        boards: ids.flatMap((id) => d.boards.find((b) => b.id === id) ?? []),
      })),
    onSettled: refresh,
    onError: showError("the order"),
  });

  const createWidget = useMutation({
    mutationFn: ({ boardId, input }: { boardId: string; input: CreateWidget }) =>
      api.home.createWidget(boardId, input),
    onSuccess: refresh,
    onError: showError("the widget"),
  });
  const updateWidget = useMutation({
    mutationFn: ({ widget, patch }: { widget: HomeWidget; patch: UpdateWidget }) =>
      api.home.updateWidget(widget.id, patch),
    onMutate: ({ widget, patch }) =>
      // A change of area or board shows after the refetch; everything else at once.
      patch.area || patch.boardId
        ? undefined
        : onWidgets((list) =>
            list.map((w) => (w.id === widget.id ? ({ ...w, ...patch } as HomeWidget) : w)),
          ),
    onSettled: refresh,
    onError: showError("the widget"),
  });
  const restoreWidget = useMutation({
    mutationFn: (id: string) => api.home.restoreWidget(id),
    onSuccess: refresh,
    onError: showError("the widget", "restored"),
  });
  const removeWidget = useMutation({
    mutationFn: (widget: HomeWidget) => api.home.removeWidget(widget.id),
    onMutate: (widget) => onWidgets((list) => list.filter((w) => w.id !== widget.id)),
    onSuccess: (_result, widget) => {
      toast("Widget deleted", {
        description: widgetLabel(widget),
        duration: 8000,
        action: { label: "Undo", onClick: () => restoreWidget.mutate(widget.id) },
      });
    },
    onSettled: refresh,
    onError: showError("the widget", "deleted"),
  });
  const moveWidget = useMutation({
    mutationFn: ({ widget, patch }: { widget: HomeWidget; patch: UpdateWidget }) =>
      api.home.updateWidget(widget.id, patch),
    onSettled: refresh,
    onError: showError("the widget"),
  });
  /**
   * FR9: moves or resizes a tile. The cache changes synchronously, in the same render as the
   * drop, so the tile never flashes back to its old cell.
   */
  function placeWidget(
    widget: HomeWidget,
    patch: Pick<UpdateWidget, "x" | "y" | "w" | "h" | "area" | "boardId">,
  ) {
    if (!patch.area || patch.area === widget.area) {
      onWidgets((list) =>
        list.map((w) => (w.id === widget.id ? ({ ...w, ...patch } as HomeWidget) : w)),
      );
      moveWidget.mutate({ widget, patch });
      return;
    }
    // FR9a: into the other grid. Top widgets live on the main board; "board" means the
    // open board, which the page passes as `boardId`.
    optimistic((d) => {
      const main = d.boards.find((b) => b.main)?.id ?? widget.boardId;
      const boardId = patch.area === "top" ? main : (patch.boardId ?? widget.boardId);
      const moved = { ...widget, ...patch, boardId } as HomeWidget;
      const without = (list: HomeWidget[]) => list.filter((w) => w.id !== widget.id);
      return {
        ...d,
        top: patch.area === "top" ? [...without(d.top), moved] : without(d.top),
        boards: d.boards.map((b) => ({
          ...b,
          widgets:
            patch.area === "board" && b.id === boardId
              ? [...without(b.widgets), moved]
              : without(b.widgets),
        })),
      };
    });
    moveWidget.mutate({ widget, patch });
  }

  return {
    query,
    createBoard,
    renameBoard,
    removeBoard,
    orderBoards,
    createWidget,
    updateWidget,
    removeWidget,
    placeWidget,
  };
}

export type HomeActions = ReturnType<typeof useHome>;
